import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
    ArrowLeft, Upload, Video, Play, Trash2, CheckCircle, 
    Clock, AlertCircle, Loader2, FileVideo, X, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';

const VideoManager = ({ onBack }) => {
    const [lessons, setLessons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(null); // lesson_id being uploaded
    const [uploadProgress, setUploadProgress] = useState(0);
    const [selectedFile, setSelectedFile] = useState(null);
    const [selectedLesson, setSelectedLesson] = useState(null);

    const loadLessons = useCallback(async () => {
        setLoading(true);
        try {
            const response = await api.get('/academy/admin/lessons');
            setLessons(response.data.lessons || []);
        } catch (error) {
            console.error('Failed to load lessons:', error);
            toast.error('Failed to load lessons');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadLessons();
    }, [loadLessons]);

    const handleFileSelect = (lessonId, event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Validate file type
        const allowedTypes = ['.mp4', '.webm', '.mov'];
        const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
        if (!allowedTypes.includes(ext)) {
            toast.error(`Invalid file type. Allowed: ${allowedTypes.join(', ')}`);
            return;
        }

        // Validate file size (500MB max)
        const maxSize = 500 * 1024 * 1024;
        if (file.size > maxSize) {
            toast.error('File too large. Maximum size is 500MB.');
            return;
        }

        setSelectedFile(file);
        setSelectedLesson(lessonId);
    };

    const handleUpload = async () => {
        if (!selectedFile || !selectedLesson) return;

        setUploading(selectedLesson);
        setUploadProgress(0);

        const formData = new FormData();
        formData.append('file', selectedFile);

        try {
            const response = await api.post(
                `/academy/upload-video/${selectedLesson}`,
                formData,
                {
                    headers: { 'Content-Type': 'multipart/form-data' },
                    onUploadProgress: (progressEvent) => {
                        const progress = Math.round(
                            (progressEvent.loaded * 100) / progressEvent.total
                        );
                        setUploadProgress(progress);
                    },
                }
            );

            toast.success(`Video uploaded successfully for lesson`);
            
            // Update lessons list
            setLessons(prev => prev.map(l => 
                l.id === selectedLesson 
                    ? { ...l, has_video: true, video_url: response.data.video_url }
                    : l
            ));

            // Reset selection
            setSelectedFile(null);
            setSelectedLesson(null);
        } catch (error) {
            console.error('Upload failed:', error);
            toast.error(error.response?.data?.detail || 'Upload failed');
        } finally {
            setUploading(null);
            setUploadProgress(0);
        }
    };

    const handleDeleteVideo = async (lessonId) => {
        if (!window.confirm('Are you sure you want to delete this video?')) return;

        try {
            await api.delete(`/academy/video/${lessonId}`);
            toast.success('Video deleted successfully');
            
            // Update lessons list
            setLessons(prev => prev.map(l => 
                l.id === lessonId 
                    ? { ...l, has_video: false, video_url: null }
                    : l
            ));
        } catch (error) {
            console.error('Delete failed:', error);
            toast.error(error.response?.data?.detail || 'Delete failed');
        }
    };

    const cancelUpload = () => {
        setSelectedFile(null);
        setSelectedLesson(null);
    };

    // Group lessons by module
    const lessonsByModule = lessons.reduce((acc, lesson) => {
        if (!acc[lesson.module]) {
            acc[lesson.module] = [];
        }
        acc[lesson.module].push(lesson);
        return acc;
    }, {});

    const moduleNames = {
        'fundamentals': 'Writing Fundamentals',
        'story-structure': 'Story Structure',
        'characters': 'Character Development',
        'worldbuilding': 'Worldbuilding',
        'editing': 'Editing & Revision',
        'publishing': 'Publishing'
    };

    const videosUploaded = lessons.filter(l => l.has_video).length;
    const totalLessons = lessons.length;

    return (
        <div className="min-h-screen bg-background">
            {/* Header */}
            <header className="sticky top-0 z-50 glass-panel border-b border-border/50">
                <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
                    <button onClick={onBack} className="flex items-center gap-2 text-muted-foreground hover:text-foreground">
                        <ArrowLeft className="h-4 w-4" />
                        Back to Admin
                    </button>
                    <div className="flex items-center gap-2">
                        <Video className="h-5 w-5 text-accent" />
                        <span className="font-semibold">Academy Video Manager</span>
                    </div>
                    <Button variant="ghost" size="sm" onClick={loadLessons} data-testid="refresh-lessons">
                        <RefreshCw className="h-4 w-4" />
                    </Button>
                </div>
            </header>

            <main className="max-w-5xl mx-auto px-6 py-8">
                {/* Stats */}
                <div className="grid grid-cols-3 gap-4 mb-8">
                    <Card>
                        <CardContent className="p-4 flex items-center gap-3">
                            <div className="p-2 bg-blue-500/10 rounded-lg">
                                <FileVideo className="h-5 w-5 text-blue-500" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold">{totalLessons}</p>
                                <p className="text-xs text-muted-foreground">Total Lessons</p>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4 flex items-center gap-3">
                            <div className="p-2 bg-green-500/10 rounded-lg">
                                <CheckCircle className="h-5 w-5 text-green-500" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold">{videosUploaded}</p>
                                <p className="text-xs text-muted-foreground">Videos Uploaded</p>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4 flex items-center gap-3">
                            <div className="p-2 bg-amber-500/10 rounded-lg">
                                <Clock className="h-5 w-5 text-amber-500" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold">{totalLessons - videosUploaded}</p>
                                <p className="text-xs text-muted-foreground">Pending Videos</p>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Upload Preview Modal */}
                {selectedFile && selectedLesson && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={cancelUpload}>
                        <Card className="w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
                            <CardHeader>
                                <div className="flex items-center justify-between">
                                    <CardTitle className="flex items-center gap-2">
                                        <Upload className="h-5 w-5 text-accent" />
                                        Upload Video
                                    </CardTitle>
                                    <Button variant="ghost" size="sm" onClick={cancelUpload}>
                                        <X className="h-4 w-4" />
                                    </Button>
                                </div>
                                <CardDescription>
                                    Uploading to lesson: {lessons.find(l => l.id === selectedLesson)?.title}
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="p-4 bg-muted/50 rounded-lg">
                                    <div className="flex items-center gap-3">
                                        <FileVideo className="h-8 w-8 text-muted-foreground" />
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium truncate">{selectedFile.name}</p>
                                            <p className="text-sm text-muted-foreground">
                                                {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {uploading && (
                                    <div className="space-y-2">
                                        <Progress value={uploadProgress} className="h-2" />
                                        <p className="text-sm text-center text-muted-foreground">
                                            {uploadProgress}% uploaded
                                        </p>
                                    </div>
                                )}

                                <div className="flex gap-2 justify-end">
                                    <Button variant="outline" onClick={cancelUpload} disabled={uploading}>
                                        Cancel
                                    </Button>
                                    <Button onClick={handleUpload} disabled={uploading} data-testid="confirm-upload">
                                        {uploading ? (
                                            <>
                                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                                Uploading...
                                            </>
                                        ) : (
                                            <>
                                                <Upload className="h-4 w-4 mr-2" />
                                                Upload Video
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                )}

                {/* Lessons List */}
                {loading ? (
                    <div className="flex items-center justify-center py-20">
                        <Loader2 className="h-8 w-8 animate-spin text-accent" />
                    </div>
                ) : (
                    <ScrollArea className="h-[600px]">
                        <div className="space-y-6">
                            {Object.entries(lessonsByModule).map(([module, moduleLessons]) => (
                                <Card key={module}>
                                    <CardHeader className="pb-3">
                                        <CardTitle className="text-lg">
                                            {moduleNames[module] || module}
                                        </CardTitle>
                                        <CardDescription>
                                            {moduleLessons.filter(l => l.has_video).length} / {moduleLessons.length} videos uploaded
                                        </CardDescription>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="space-y-3">
                                            {moduleLessons.map((lesson) => (
                                                <div 
                                                    key={lesson.id}
                                                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                                                    data-testid={`lesson-row-${lesson.id}`}
                                                >
                                                    <div className="flex items-center gap-3 flex-1">
                                                        <div className={`p-2 rounded-lg ${lesson.has_video ? 'bg-green-500/10' : 'bg-muted'}`}>
                                                            {lesson.has_video ? (
                                                                <Play className="h-5 w-5 text-green-500" />
                                                            ) : (
                                                                <AlertCircle className="h-5 w-5 text-muted-foreground" />
                                                            )}
                                                        </div>
                                                        <div>
                                                            <p className="font-medium">{lesson.title}</p>
                                                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                                                <span>{lesson.duration_minutes} min</span>
                                                                {lesson.has_video ? (
                                                                    <Badge className="bg-green-500/10 text-green-600 border-green-500/20">
                                                                        Video Ready
                                                                    </Badge>
                                                                ) : (
                                                                    <Badge variant="secondary">No Video</Badge>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="flex items-center gap-2">
                                                        {lesson.has_video && (
                                                            <Button 
                                                                size="sm" 
                                                                variant="destructive"
                                                                onClick={() => handleDeleteVideo(lesson.id)}
                                                                data-testid={`delete-video-${lesson.id}`}
                                                            >
                                                                <Trash2 className="h-4 w-4" />
                                                            </Button>
                                                        )}
                                                        <div className="relative">
                                                            <Input
                                                                type="file"
                                                                accept=".mp4,.webm,.mov"
                                                                className="absolute inset-0 opacity-0 cursor-pointer"
                                                                onChange={(e) => handleFileSelect(lesson.id, e)}
                                                                disabled={uploading === lesson.id}
                                                                data-testid={`file-input-${lesson.id}`}
                                                            />
                                                            <Button 
                                                                size="sm" 
                                                                variant={lesson.has_video ? "outline" : "default"}
                                                                disabled={uploading === lesson.id}
                                                            >
                                                                {uploading === lesson.id ? (
                                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                                ) : (
                                                                    <>
                                                                        <Upload className="h-4 w-4 mr-1" />
                                                                        {lesson.has_video ? 'Replace' : 'Upload'}
                                                                    </>
                                                                )}
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    </ScrollArea>
                )}
            </main>
        </div>
    );
};

export default VideoManager;
